import type {
    BuildingSpecification,
    OpeningSpecification,
    PlanPoint,
    SlabSpecification,
    WallSpecification
} from 'app/modules/ifc-editor/types/buildingSpecification';

/** Endpoints closer than this are treated as one junction (the backend joins at 1e-6; plans are rounded to mm). */
export const JUNCTION_TOLERANCE = 0.001;
const MIN_WALL_LENGTH = 0.05;
const MIN_THICKNESS = 0.05;
const MAX_THICKNESS = 3;

export const distance = (a: PlanPoint, b: PlanPoint) => Math.hypot(b.x - a.x, b.y - a.y);
export const samePoint = (a: PlanPoint, b: PlanPoint, tolerance = JUNCTION_TOLERANCE) => distance(a, b) <= tolerance;
export const wallLength = (wall: WallSpecification) => distance(wall.start, wall.end);
const roundMillimetres = (value: number) => Math.round(value * 1000) / 1000;
export const roundPoint = (point: PlanPoint): PlanPoint => ({
    x: roundMillimetres(point.x),
    y: roundMillimetres(point.y)
});

/** Unit direction from start to end, and its left normal. */
export const wallAxes = (wall: WallSpecification) => {
    const length = wallLength(wall) || 1;
    const ux = (wall.end.x - wall.start.x) / length;
    const uy = (wall.end.y - wall.start.y) / length;
    return { ux, uy, nx: -uy, ny: ux };
};

/** Footprint rectangle of the centreline wall (junction mitres are applied by the backend). */
export const wallOutline = (wall: WallSpecification): PlanPoint[] => {
    const { nx, ny } = wallAxes(wall);
    const half = wall.thickness / 2;
    return [
        { x: wall.start.x + nx * half, y: wall.start.y + ny * half },
        { x: wall.end.x + nx * half, y: wall.end.y + ny * half },
        { x: wall.end.x - nx * half, y: wall.end.y - ny * half },
        { x: wall.start.x - nx * half, y: wall.start.y - ny * half }
    ];
};

/** Shows centimetres, or millimetres when the value needs them. */
export const formatLength = (metres: number) => {
    const centimetres = Math.round(metres * 100);
    return `${Math.abs(metres * 100 - centimetres) < 1e-6 ? metres.toFixed(2) : metres.toFixed(3)} m`;
};

export const getBounds = (points: PlanPoint[]) => {
    if (points.length === 0) {
        return { minX: -5, minY: -5, maxX: 5, maxY: 5 };
    }
    return points.reduce(
        (bounds, point) => ({
            minX: Math.min(bounds.minX, point.x),
            minY: Math.min(bounds.minY, point.y),
            maxX: Math.max(bounds.maxX, point.x),
            maxY: Math.max(bounds.maxY, point.y)
        }),
        { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity }
    );
};

export const storeyPoints = (specification: BuildingSpecification, storey: string) => [
    ...specification.walls.filter((wall) => wall.storey === storey).flatMap(wallOutline),
    ...specification.slabs.filter((slab) => slab.storey === storey).flatMap((slab) => slab.outline)
];

const replaceWall = (specification: BuildingSpecification, index: number, wall: WallSpecification) => ({
    ...specification,
    walls: specification.walls.map((existing, wallIndex) => (wallIndex === index ? wall : existing))
});

export const updateWall = (
    specification: BuildingSpecification,
    index: number,
    changes: Partial<WallSpecification>
): BuildingSpecification => replaceWall(specification, index, { ...specification.walls[index], ...changes });

/** Moves one junction: every wall end on the same storey at the original point follows, so joins stay closed. */
export const moveJunction = (
    specification: BuildingSpecification,
    storey: string,
    from: PlanPoint,
    to: PlanPoint
): BuildingSpecification => {
    const target = roundPoint(to);
    return {
        ...specification,
        walls: specification.walls.map((wall) =>
            wall.storey !== storey
                ? wall
                : {
                      ...wall,
                      start: samePoint(wall.start, from) ? target : wall.start,
                      end: samePoint(wall.end, from) ? target : wall.end
                  }
        )
    };
};

/**
 * Changes a wall's length by moving its end along the wall. Like a CAD stretch, walls connected beyond that end
 * move with it (a perpendicular wall is translated, the wall after it lengthened) and slab vertices beyond the end
 * follow, so an orthogonal outline stays orthogonal. The start point never moves.
 */
export const setWallLength = (
    specification: BuildingSpecification,
    index: number,
    length: number
): BuildingSpecification => {
    const wall = specification.walls[index];
    const oldLength = wallLength(wall);
    if (!Number.isFinite(length) || length <= 0 || oldLength === 0) {
        return specification;
    }
    const { ux, uy } = wallAxes(wall);
    const dx = ux * (length - oldLength);
    const dy = uy * (length - oldLength);
    const projection = (point: PlanPoint) => (point.x - wall.start.x) * ux + (point.y - wall.start.y) * uy;
    const limit = oldLength - JUNCTION_TOLERANCE;

    // Breadth-first over junctions, only through points beyond the moved end.
    const moved: PlanPoint[] = [wall.end];
    const isMoved = (point: PlanPoint) => moved.some((candidate) => samePoint(candidate, point));
    for (let changed = true; changed;) {
        changed = false;
        for (const other of specification.walls) {
            if (other.storey !== wall.storey) {
                continue;
            }
            for (const [from, to] of [
                [other.start, other.end],
                [other.end, other.start]
            ]) {
                if (isMoved(from) && !isMoved(to) && projection(to) >= limit) {
                    moved.push(to);
                    changed = true;
                }
            }
        }
    }

    const shift = (point: PlanPoint) => (isMoved(point) ? roundPoint({ x: point.x + dx, y: point.y + dy }) : point);
    const maxHalfThickness = Math.max(
        ...specification.walls.filter((other) => other.storey === wall.storey).map((other) => other.thickness / 2)
    );
    return {
        ...specification,
        walls: specification.walls.map((other) =>
            other.storey === wall.storey ? { ...other, start: shift(other.start), end: shift(other.end) } : other
        ),
        slabs: specification.slabs.map((slab) =>
            slab.storey !== wall.storey
                ? slab
                : {
                      ...slab,
                      outline: slab.outline.map((point) =>
                          projection(point) >= oldLength - maxHalfThickness - JUNCTION_TOLERANCE
                              ? roundPoint({ x: point.x + dx, y: point.y + dy })
                              : point
                      )
                  }
        )
    };
};

/** Snaps to another junction first, then horizontal/vertical alignment with an anchor, then to whole millimetres. */
export const snapPoint = (
    point: PlanPoint,
    candidates: PlanPoint[],
    anchor: PlanPoint | null,
    tolerance: number
): PlanPoint => {
    const nearest = candidates.reduce<{ point: PlanPoint; distance: number } | null>((best, candidate) => {
        const candidateDistance = distance(candidate, point);
        return candidateDistance <= tolerance && (!best || candidateDistance < best.distance)
            ? { point: candidate, distance: candidateDistance }
            : best;
    }, null);
    if (nearest) {
        return nearest.point;
    }
    const snapped = { ...point };
    if (anchor && Math.abs(point.x - anchor.x) <= tolerance) {
        snapped.x = anchor.x;
    }
    if (anchor && Math.abs(point.y - anchor.y) <= tolerance) {
        snapped.y = anchor.y;
    }
    return roundPoint(snapped);
};

export const renameStorey = (
    specification: BuildingSpecification,
    oldName: string,
    newName: string
): BuildingSpecification => ({
    ...specification,
    storeys: specification.storeys.map((storey) => (storey.name === oldName ? { ...storey, name: newName } : storey)),
    walls: specification.walls.map((wall) => (wall.storey === oldName ? { ...wall, storey: newName } : wall)),
    slabs: specification.slabs.map((slab) => (slab.storey === oldName ? { ...slab, storey: newName } : slab))
});

export const nextWallId = (specification: BuildingSpecification) => {
    const highest = specification.walls.reduce((max, wall) => {
        const match = /(\d+)$/.exec(wall.id);
        return match ? Math.max(max, Number(match[1])) : max;
    }, 0);
    let candidate = highest + 1;
    while (specification.walls.some((wall) => wall.id === `W${candidate}`)) {
        candidate++;
    }
    return `W${candidate}`;
};

export const createWall = (specification: BuildingSpecification, storey: string, at: PlanPoint): WallSpecification => {
    const template = specification.walls.find((wall) => wall.storey === storey);
    return {
        id: nextWallId(specification),
        storey,
        start: roundPoint(at),
        end: roundPoint({ x: at.x + 3, y: at.y }),
        thickness: template?.thickness ?? 0.25,
        baseOffset: template?.baseOffset ?? 0,
        height: template?.height ?? null,
        openings: []
    };
};

export const createOpening = (wall: WallSpecification): OpeningSpecification => {
    const length = wallLength(wall);
    const width = Math.min(1, Math.max(0.1, length / 3));
    return { kind: 'Door', offset: roundMillimetres((length - width) / 2), width, height: 2.1, sillHeight: 0 };
};

export const wallHeight = (specification: BuildingSpecification, wall: WallSpecification) =>
    wall.height ?? specification.storeys.find((storey) => storey.name === wall.storey)?.height ?? 0;

/** Immediate feedback for the obvious mistakes; the backend repeats the full validation when saving. */
export const getWallIssues = (specification: BuildingSpecification, wall: WallSpecification): string[] => {
    const issues: string[] = [];
    const length = wallLength(wall);
    if (length < MIN_WALL_LENGTH) {
        issues.push(`Length must be at least ${MIN_WALL_LENGTH} m.`);
    }
    if (!(wall.thickness >= MIN_THICKNESS && wall.thickness <= MAX_THICKNESS)) {
        issues.push(`Thickness must be between ${MIN_THICKNESS} and ${MAX_THICKNESS} m.`);
    }
    const height = wallHeight(specification, wall);
    for (const opening of wall.openings) {
        if (opening.offset < 0 || opening.offset + opening.width > length + JUNCTION_TOLERANCE) {
            issues.push('An opening lies outside the wall.');
        }
        if (opening.sillHeight + opening.height > height + JUNCTION_TOLERANCE) {
            issues.push('An opening is higher than the wall.');
        }
    }
    return issues;
};

export const getSlabIssues = (slab: SlabSpecification): string[] => {
    const issues: string[] = [];
    if (slab.outline.length < 3) {
        issues.push('An outline needs at least three points.');
    }
    if (!(slab.thickness >= MIN_THICKNESS && slab.thickness <= MAX_THICKNESS)) {
        issues.push(`Thickness must be between ${MIN_THICKNESS} and ${MAX_THICKNESS} m.`);
    }
    return issues;
};

/** Fills defaults the API may omit so the editor can rely on a complete shape. */
export const normalizeSpecification = (specification: BuildingSpecification): BuildingSpecification => ({
    ...specification,
    storeys: specification.storeys ?? [],
    assumptions: specification.assumptions ?? [],
    slabs: (specification.slabs ?? []).map((slab) => ({ ...slab, outline: slab.outline ?? [] })),
    walls: (specification.walls ?? []).map((wall) => ({
        ...wall,
        baseOffset: wall.baseOffset ?? 0,
        height: wall.height ?? null,
        openings: (wall.openings ?? []).map((opening) => ({ ...opening, sillHeight: opening.sillHeight ?? 0 }))
    }))
});
